/**
 * V3 category-tree helpers.
 *
 * The V3 categories endpoint (`/coach/explore/v3/categories`) returns the whole
 * flat category set for the athlete's gym (it ignores the legacy `root: true`
 * flag). It also has no slug lookup — only `parentId` (a UUID). So everything
 * coach.kinastic.com used to get "for free" from the polymorphic V1 surface
 * (root detection, slug -> id resolution, children-of-a-category) now has to be
 * derived client-side from that flat list.
 *
 * This module owns that derivation and a small in-memory cache so the flat list
 * is fetched once per TTL window and reused across the static API objects and
 * the non-suspense hooks. (Suspense hooks fetch via react-query directly but
 * reuse the pure derivation helpers below.)
 */
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { ExploreCategory, createExploreCategory } from './ExploreCategory';

const V3_CATEGORIES_PATH = '/coach/explore/v3/categories';
const PAGE_SIZE = 200; // matches admin-web's loadRootCategories; covers a gym's full set + globals
const TTL = 5 * 60 * 1000;

let cache: { at: number; promise: Promise<ExploreCategory[]> } | undefined;

/** Fetch the full flat V3 category list (cached for the TTL window). */
export function getAllExploreCategoriesV3(force = false): Promise<ExploreCategory[]> {
  if (force || !cache || Date.now() - cache.at > TTL) {
    cache = {
      at: Date.now(),
      promise: HttpBackend.get(V3_CATEGORIES_PATH, { size: PAGE_SIZE })
        .then((res) => (res ?? []).map((c: any) => createExploreCategory(c)))
        .catch((e) => {
          cache = undefined; // don't cache failures
          throw e;
        }),
    };
  }
  return cache.promise;
}

/** Resolve a category identifier (slug) OR id to its category UUID. */
export function resolveCategoryId(all: ExploreCategory[], identifierOrId?: string): string | undefined {
  if (!identifierOrId) return undefined;
  const byId = all.find((c) => c.id === identifierOrId);
  if (byId) return byId.id;
  return all.find((c) => c.identifier === identifierOrId)?.id;
}

/**
 * Derive root categories client-side: a category is a root iff no other
 * category lists it as a child. Mirrors admin-web / kinasticCoach.
 */
export function deriveRootCategories(all: ExploreCategory[]): ExploreCategory[] {
  const childIds = new Set(all.flatMap((c) => c.childrenIds ?? []));
  return all.filter((c) => !childIds.has(c.id));
}

/** Direct child categories of the given parent (resolved from identifier or id). */
export function childCategoriesOf(all: ExploreCategory[], parentIdentifierOrId?: string): ExploreCategory[] {
  const parentId = resolveCategoryId(all, parentIdentifierOrId);
  if (!parentId) return [];
  return all.filter((c) => (c.parentIds ?? []).includes(parentId));
}

/** Async convenience: resolve a slug/id to a UUID using the cached list. */
export async function resolveCategoryIdAsync(identifierOrId?: string): Promise<string | undefined> {
  if (!identifierOrId) return undefined;
  const all = await getAllExploreCategoriesV3();
  return resolveCategoryId(all, identifierOrId);
}

/**
 * The resolved category's UUID plus all descendant category UUIDs (breadth-first).
 * V3's `parentId` filter matches DIRECT children only, so subtree-scoped queries
 * must fan out over these ids. Returns [] for an unresolvable slug/id.
 */
export function subtreeCategoryIdsOf(all: ExploreCategory[], identifierOrId?: string): string[] {
  const rootId = resolveCategoryId(all, identifierOrId);
  if (!rootId) return [];
  const ids = [rootId];
  const seen = new Set(ids);
  for (let i = 0; i < ids.length; i++) {
    for (const child of all) {
      if (!seen.has(child.id) && (child.parentIds ?? []).includes(ids[i])) {
        seen.add(child.id);
        ids.push(child.id);
      }
    }
  }
  return ids;
}

/** Async convenience: subtree ids from the cached list. */
export async function subtreeCategoryIdsOfAsync(identifierOrId?: string): Promise<string[]> {
  if (!identifierOrId) return [];
  const all = await getAllExploreCategoriesV3();
  return subtreeCategoryIdsOf(all, identifierOrId);
}
